import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import {SelectBox} from "devextreme-react";
import {Column, DataGrid, Paging} from "devextreme-react/data-grid";
import {useSearchParams} from "react-router-dom";
import apiClient from "../../services/axios";
import LoaderService from "../../RetroAssets.System/LoaderService";

// Earliest year offered in the selector. The query returns portfolio rows from
// CY2008 - docs/NOI-QUERY-01.md section 2 - and 2007 is kept as the floor only
// so the selector matches the Occupancy one.
const FIRST_YEAR = 2007;

// Grid sizing. Lifted from RetroAssets/Occupancy/index.js, not from the property
// listing: the listing computes cl.setDataGridHeight() - 23 once, on data
// arrival, from a constant calibrated against its own chrome (page title row,
// tab strip, grid). This screen carries a part-year banner, a summary strip and
// a footnote that the listing does not, and two of those three appear and
// disappear with the selected year, so a constant cannot describe them and a
// single measurement cannot survive them.
//
// Everything variable is therefore measured at layout time: the top of the grid
// itself, everything drawn below it, and the fixed bottom navigation bar. The
// constants below are used only before the grid exists - during a load, an
// error, or a year with no data - and each one is a named stylesheet rule.
const MIN_GRID_HEIGHT = 200;
const NOTE_MARGIN = 8;          // footnote mt-2
const CARD_PADDING = 16;        // Card p-3, bottom half
const WRAPPER_PADDING = 16;     // div.main-wrapper p-3, bottom half
const CONTENT_TAIL = 56;        // .content-wrapper padding-bottom, global.scss:10-13

// Tab keys. Company tabs key on OwnerECO_ID, so these two must not collide with
// a GUID.
const TAB_ALL = "ALL";
const TAB_UNASSIGNED = "UNASSIGNED";

// GET /api/Property/GetPropertyNOI returns the composite envelope, double
// encoded, positionally indexed:
//
//   [ {"Data": [ ...rows... ]}, {"Message": "..."}, {"Coverage": {...}} ]
//
// Three keys, not the two Occupancy carries. HTTP is 200 for success and for
// business failure alike, so nothing below reads the status.
//
// Message is NOT an error flag on this endpoint, which is the one place the
// Occupancy parse cannot be copied verbatim. A year still running returns a full
// 25-row Data AND a message cautioning that the figures are part-year, so
// treating any message as a failure would blank the current year. The three
// states are separated on Coverage instead:
//
//   Coverage.Year null                     the request was rejected; Message is
//                                          the reason. An error.
//   Data empty, Coverage.Year present      the year has not begun. Explicitly
//                                          no data - never a zero portfolio.
//   Data present                           figures. Message, if any, is the
//                                          part-year caution, not a failure.
const isPresent = value => value !== null && value !== undefined && value !== "";

// Every figure the endpoint returns is AED. PropertyNOI.txt divides each
// transaction by the rate captured on that row - Ext_Total / NULLIF(CR, 0) at
// :209 and Amount / COALESCE(NULLIF(txCr.CR, 0), NULLIF(Currency.CR, 0), 1) at
// :294 - so the 65 non-AED transaction rows in the database arrive converted.
// Nothing on this screen holds a native amount, and there is no second currency
// to switch to; AED is stated in the band captions, on the summary strip and in
// the footnote rather than repeated in every cell.
const money = value => (isPresent(value)
    ? Number(value).toLocaleString("en-AE", {minimumFractionDigits: 2, maximumFractionDigits: 2})
    : "—");

// Margin is NOI over operating income. Zero income is not a zero margin - eight
// properties earn nothing in a part year and dividing by their income would
// print 0.00% or Infinity for both.
const marginText = (noi, income) => {
    if (!isPresent(noi) || !isPresent(income) || Number(income) === 0) return "N/A";
    return (Number(noi) / Number(income) * 100).toFixed(2) + "%";
};

// The Coverage date arrives as yyyy-MM-dd. Parsed by hand rather than through
// new Date(string): the browser reads a bare yyyy-MM-dd as UTC midnight and
// renders the day before in any negative offset.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const coveredDateText = value => {
    if (!isPresent(value)) return "";
    const parts = String(value).substring(0, 10).split("-");
    if (parts.length !== 3) return String(value);
    const month = MONTHS[Number(parts[1]) - 1];
    if (!month) return String(value);
    return Number(parts[2]) + " " + month + " " + parts[0];
};

const NOI_COLUMNS = [
    "OperatingIncomeRent", "OperatingIncomeSTR", "OperatingIncomeTotal",
    "OperatingExpenseMaintenance", "OperatingExpenseAgencyFees", "OperatingExpenseSTRWithheld",
    "OperatingExpenseTotal", "NOI", "CapitalExpenditure", "CapitalPurchaseSale"];

// The yield columns that ADD. The two ratios are deliberately absent: a yield
// does not sum, and a company subtotal has to be rebuilt from these as
// SUM(numerator) / SUM(denominator) rather than accumulated from the per-row
// percentages. Counts are 1/0 per property row, so they add into a property
// count the same way the money columns add into a total.
const YIELD_SUM_COLUMNS = [
    "CostBasisAED", "YieldPropertyCount", "YieldNumeratorIncome", "YieldNumeratorNOI",
    "YieldDenominator", "CommittedCapitalNotInService", "CommittedCapitalPropertyCount"];

const sumRows = rows => {
    const total = {};
    NOI_COLUMNS.concat(YIELD_SUM_COLUMNS).forEach(name => {
        total[name] = rows.reduce((carry, row) => carry + (isPresent(row[name]) ? Number(row[name]) : 0), 0);
    });
    return total;
};

// Yield on cost, as a PERCENTAGE, or null when this row set carries none.
//
// Two sources, in order. Under ALL the summary figures ARE the server's
// Portfolio row, which already carries the ratio it computed over the yield
// set - that is the figure the control tables quote and it is used as is.
// Under a company tab the figures are a client subtotal with no ratio on them,
// so the ratio is rebuilt from the summed numerator and denominator. It is
// never the mean of the per-row percentages, which would weight a 350k studio
// the same as a 3.8m villa.
//
// null, never 0, on three states that must not print as a percentage: the year
// is incomplete and the server suppressed every yield column at source; no row
// in scope is in the yield set; or the denominator summed to zero. The caller
// decides what to say about each - see yieldCard.
const yieldPercent = (figures, ratioField, numeratorField) => {
    if (!figures) return null;
    if (isPresent(figures[ratioField])) return Number(figures[ratioField]) * 100;
    const numerator = figures[numeratorField];
    const denominator = figures["YieldDenominator"];
    if (!isPresent(numerator) || !isPresent(denominator) || Number(denominator) === 0) return null;
    return Number(numerator) / Number(denominator) * 100;
};

const percentText = value => (value === null ? "" : value.toFixed(2) + "%");

const propertyWord = count => (Number(count) === 1 ? "property" : "properties");

function NOI() {
    const currentYear = new Date().getFullYear();
    const [searchParams] = useSearchParams();

    // A year may arrive on the query string. The selector offers finished and
    // running years only, but the endpoint answers for any year in 1900-2999 and
    // "that year has not begun" is one of its four documented states, so a
    // ?year= link has to be able to reach it.
    const linkedYear = useMemo(() => {
        const raw = searchParams.get("year");
        if (!isPresent(raw)) return null;
        if (!/^\s*\d{4}\s*$/.test(raw)) return null;
        return Number(raw.trim());
    }, [searchParams]);

    // chosen stays false until the operator picks a year, so the first read
    // sends no year at all and the server applies its own current calendar year.
    const [selection, setSelection] = useState(
        linkedYear === null
            ? {year: currentYear, chosen: false}
            : {year: linkedYear, chosen: true});
    const [rows, setRows] = useState([]);
    const [coverage, setCoverage] = useState(null);
    const [notice, setNotice] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [currentTab, setCurrentTab] = useState(TAB_ALL);
    const [height, setHeight] = useState(MIN_GRID_HEIGHT);

    // The block the grid and its footnote sit in, and the footnote itself.
    const gridAreaRef = useRef(null);
    const noteRef = useRef(null);
    // Smallest height computed since the last deliberate chrome change. The
    // footnote rewraps when the page scrollbar appears or disappears, and its
    // height feeds the next measurement, so a grid sized to exactly fill the
    // viewport can flip between two values forever. Keeping the floor breaks
    // that: a grid one footnote-line short never brings the scrollbar back.
    const floorRef = useRef(Infinity);

    const years = useMemo(() => {
        const list = [];
        // A linked year ahead of today is offered too, so the SelectBox is never
        // showing a value it does not hold.
        if (linkedYear !== null && linkedYear > currentYear)
            list.push(linkedYear);
        for (let y = currentYear; y >= FIRST_YEAR; y--)
            list.push(y);
        return list;
    }, [currentYear, linkedYear]);

    const measureGrid = useCallback(() => {
        const area = gridAreaRef.current;
        if (!area) return;

        const viewport = document.documentElement.clientHeight;
        const scrollY = window.scrollY;

        const nav = document.getElementsByClassName("bottom-navigation")[0];
        // The bar is fixed-bottom and overlays the page, so its height has to
        // come off the grid rather than out of the document flow. Measured, not
        // assumed: it is taller once the navbar collapses below lg.
        const navHeight = nav ? nav.offsetHeight : CONTENT_TAIL;

        // Where the grid starts, and how much page sits below its last pixel -
        // the footnote, the card's own padding and border, and the wrapper's
        // padding. All read off the rendered elements at the same instant,
        // which is what makes them independent of the height the grid currently
        // happens to have: two rects taken together are right whatever that
        // height is, so a stale height cannot feed itself back in.
        //
        // Occupancy counts the top down from the grid AREA instead, and the
        // area is .tab-content pt-1 - so its border box starts four pixels
        // above the grid, and those four pixels are the page scrollbar this
        // screen would otherwise have had at 1920. Measuring the grid removes
        // the constant rather than adding a fifth one.
        //
        // Document scrollHeight is deliberately NOT used for this. The content
        // wrapper carries min-height: calc(100vh - 60px) (global.scss:10-13),
        // so a short page is pinned to the viewport and scrollHeight stops
        // describing the content - it would report a tail that is mostly empty
        // space and drive the grid smaller on every pass.
        const grid = area.querySelector(".dx-datagrid");
        const note = noteRef.current;
        const card = area.closest(".card");
        const wrapper = area.closest(".main-wrapper");

        let top;
        let toCardBottom;    // grid bottom -> last drawn pixel of the card
        let toWrapperBottom; // grid bottom -> end of the page's flow content
        if (grid && card && wrapper) {
            const rect = grid.getBoundingClientRect();
            // Document relative, not viewport relative. A page that is currently
            // scrolled would otherwise feed its own scroll offset back into the
            // next height and grow without bound.
            top = rect.top + scrollY;
            toCardBottom = card.getBoundingClientRect().bottom - rect.bottom;
            toWrapperBottom = wrapper.getBoundingClientRect().bottom - rect.bottom;
        } else {
            // No grid yet - loading, an error, or a year with no data. Fall back
            // to the area and the named paddings so the first pass still lands
            // close, and let the pass after the grid draws correct it.
            const padTop = parseFloat(window.getComputedStyle(area).paddingTop) || 0;
            top = area.getBoundingClientRect().top + scrollY + padTop;
            toCardBottom = (note ? note.offsetHeight : 0) + NOTE_MARGIN + CARD_PADDING;
            toWrapperBottom = toCardBottom + WRAPPER_PADDING;
        }

        // Two separate constraints, and the grid has to satisfy both. The
        // document must not outgrow the viewport, which costs the content
        // wrapper's tail padding on top of everything in the flow; and the last
        // drawn pixel of the card must clear the fixed bar, which costs the
        // bar's height instead. The wrapper's own trailing padding is empty, so
        // the bar may overlap it - the card border may not.
        const reserve = Math.max(toWrapperBottom + CONTENT_TAIL, toCardBottom + navHeight);

        // Floor, not round. A half pixel of line box left over is a page
        // scrollbar, and one pixel short of the viewport is invisible.
        let next = Math.floor(viewport - top - reserve);
        next = Math.min(next, floorRef.current);
        floorRef.current = next;
        next = Math.max(MIN_GRID_HEIGHT, next);
        setHeight(prev => (prev === next ? prev : next));
    }, []);

    // No dependency list. Every state this component holds moves the chrome
    // above the grid - the part-year banner, the summary figures, the tab strip,
    // the loading and error bodies - and the effect settles to a fixed point
    // after one further pass, because a matching height sets no state.
    useLayoutEffect(() => {
        measureGrid();
    });

    useEffect(() => {
        const onResize = () => {
            floorRef.current = Infinity;
            measureGrid();
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [measureGrid]);

    useEffect(() => {
        // cancelled guards every setState below, so navigating away mid-request
        // cannot land an update on an unmounted component.
        let cancelled = false;
        setLoading(true);
        setError("");
        setNotice("");
        // A new read replaces the banner, the summary figures and the tab strip,
        // so the floor is released and re-established against the new chrome.
        floorRef.current = Infinity;
        LoaderService.setData(true);

        const url = selection.chosen
            ? "/Property/GetPropertyNOI?" + new URLSearchParams({year: selection.year})
            : "/Property/GetPropertyNOI";

        apiClient
            .get(url)
            .then(response => {
                LoaderService.setData(false);
                if (cancelled) return;
                try {
                    // Double encoded: the body is a JSON string holding the payload.
                    const payload = JSON.parse(response.data);
                    const message = payload[1] ? payload[1]["Message"] : "";
                    const cover = payload[2] ? payload[2]["Coverage"] : null;
                    const data = payload[0] ? payload[0]["Data"] : null;
                    const list = Array.isArray(data) ? data : [];

                    setCoverage(cover && isPresent(cover["Year"]) ? cover : null);

                    if (!cover || !isPresent(cover["Year"])) {
                        // The request was rejected. Business failure still
                        // arrives as HTTP 200, which is why the payload decides.
                        console.log("GetPropertyNOI: " + message);
                        setRows([]);
                        setError(message || "The request was rejected by the server.");
                    } else {
                        setRows(list);
                        // Carried whether or not there are rows: on a year in
                        // progress it is the part-year caution, on a year not
                        // begun it is the reason there is nothing to show.
                        setNotice(message || "");
                        // Height is not set here. The layout effect measures it
                        // after the banner, the summary strip and the tab strip
                        // have drawn, which is the only point at which the space
                        // left for the grid is actually known.
                    }
                } catch (e) {
                    console.log(e);
                    setRows([]);
                    setCoverage(null);
                    setError("NOI data could not be read. See the browser console for details.");
                }
                setLoading(false);
            })
            .catch(err => {
                // No alert - the failure is rendered in the page body instead.
                LoaderService.setData(false);
                console.log(err);
                if (cancelled) return;
                setRows([]);
                setCoverage(null);
                setError("NOI could not be loaded. See the browser console for details.");
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selection]);

    // RowType is filtered here, before anything binds, and a row carrying no
    // ECO_ID is dropped with it. ECO_ID is the grid key: Code is display only
    // and is not unique - 001 covers both a Retrohomes property and an SnB one.
    const propertyRows = useMemo(
        () => rows.filter(r => r["RowType"] === "Property" && isPresent(r["ECO_ID"])),
        [rows]);
    const portfolioRow = useMemo(
        () => rows.find(r => r["RowType"] === "Portfolio"),
        [rows]);

    // Part year, and year not begun. Both come off Coverage, never off the
    // figures: a part year has exactly the same shape, column set and precision
    // as a finished one and nothing inside Data distinguishes them.
    const partYear = !!(coverage && coverage["IsComplete"] === false && isPresent(coverage["LastDateCovered"]));
    const notBegun = !!(coverage && !isPresent(coverage["LastDateCovered"]) && propertyRows.length === 0);
    const coveredTo = partYear ? coveredDateText(coverage["LastDateCovered"]) : "";
    const partYearShort = partYear ? "part year to " + coveredTo : "";

    // Company tabs, derived from the rows already loaded and keyed the same way
    // Occupancy keys them. OwnerName is the CURRENT DefaultOwner mapping, not
    // ownership as at the transaction date - the same relationship the shipped
    // Property Listings tabs use - which is why the 2025-09-22 Cavoria transfer
    // does not move historical figures between these columns. Stated in the
    // footnote as well; recorded here so it is not read as a defect.
    const tabs = useMemo(() => {
        const companies = new Map();
        let unassigned = 0;
        propertyRows.forEach(row => {
            if (!isPresent(row["OwnerECO_ID"])) {
                // Never silently dropped - it gets its own tab so the counts
                // always reconcile back to the ALL count, and so its figures
                // stay inside a company subtotal somewhere.
                unassigned++;
                return;
            }
            const key = String(row["OwnerECO_ID"]);
            const found = companies.get(key);
            if (found)
                found.count++;
            else
                companies.set(key, {
                    key: key,
                    name: isPresent(row["OwnerName"]) ? row["OwnerName"] : key,
                    count: 1
                });
        });
        // Same order as the listing tabs, which sort on the account name.
        const list = Array.from(companies.values()).sort((a, b) => a.name.localeCompare(b.name));
        list.unshift({key: TAB_ALL, name: "ALL", count: propertyRows.length});
        if (unassigned > 0)
            list.push({key: TAB_UNASSIGNED, name: "Unassigned", count: unassigned});
        return list;
    }, [propertyRows]);

    // Filtering is client side over rows already held. Changing tab must not
    // fire a request - only the year selector does that.
    const visibleRows = useMemo(() => {
        if (currentTab === TAB_ALL) return propertyRows;
        if (currentTab === TAB_UNASSIGNED) return propertyRows.filter(r => !isPresent(r["OwnerECO_ID"]));
        return propertyRows.filter(r => isPresent(r["OwnerECO_ID"]) && String(r["OwnerECO_ID"]) === currentTab);
    }, [propertyRows, currentTab]);

    useEffect(() => {
        // A tab that no longer exists in the loaded set falls back to ALL, so the
        // grid can never end up filtered by a company that is not on the strip.
        if (!tabs.some(t => t.key === currentTab))
            setCurrentTab(TAB_ALL);
    }, [tabs, currentTab]);

    // The summary strip follows the tab, unlike Occupancy's - whose two figures
    // are computed server side over the whole portfolio and cannot be rescoped.
    // Here every figure is additive, so a company tab shows that company's own
    // subtotal. Under ALL the server's own Portfolio row is used rather than a
    // client sum of the property rows; the two agree to the cent in CY2024,
    // CY2025 and CY2026, and the server row is the one the control tables quote.
    const summary = useMemo(() => {
        if (currentTab === TAB_ALL && portfolioRow)
            return {figures: portfolioRow, scope: "All companies"};
        const tab = tabs.find(t => t.key === currentTab);
        return {
            figures: sumRows(visibleRows),
            scope: tab ? tab.name : "All companies"
        };
    }, [currentTab, portfolioRow, tabs, visibleRows]);

    const yearChanged = e => {
        if (!e.event) return;
        if (!isPresent(e.value)) return;
        setSelection({year: e.value, chosen: true});
    };

    // The two-line treatment the property listing uses, reproduced rather than
    // reinvented: PropertiesComponent.js:436-441 renders the name in bold on the
    // first line and the second field beneath it, as two stacked col-12 divs.
    // That screen registers the markup as the DevExtreme "propertyNameTemplate"
    // and a VM_Column row selects it; this grid has no VM_Layout row to hang a
    // template on, so the same markup is produced through cellRender.
    const PropertyNameCell = props => (
        <div>
            <div className="col-12"><b>{props.data["Name"]}</b></div>
            <div className="col-12">{props.data["Code"]}</div>
        </div>
    );

    const OwnerCell = props => (
        <span>{isPresent(props.data["OwnerName"]) ? props.data["OwnerName"] : "Unassigned"}</span>
    );

    const moneyCell = field => props => <span>{money(props.data[field])}</span>;

    // A negative NOI in a year still running is normally cash timing, not a
    // loss: service charges are billed early in the calendar year and rent is
    // recognised on the cheque date, which falls on the tenancy anniversary.
    // Every one of the eight CY2026 negatives is zero income against real
    // expense, and every one of those properties was strongly positive in
    // CY2025. So in a part year a negative is drawn in body colour with the
    // reason beside it, and the red loss treatment is kept for a finished year,
    // where a negative NOI is a loss and reads as one.
    const NoiCell = props => {
        const value = props.data["NOI"];
        const negative = isPresent(value) && Number(value) < 0;
        if (negative && partYear)
            return (
                <div>
                    <div>{money(value)}</div>
                    <div className="fs-7 text-secondary">part-year timing, not a loss</div>
                </div>
            );
        return <span className={negative ? "text-danger fw-bold" : "fw-bold"}>{money(value)}</span>;
    };

    // A yield cell. NEVER a dash: this is a numeric column and a dash in one
    // reads as zero, while zero is a real value the column can carry - a held,
    // available property that earned nothing in the year genuinely returns
    // 0.00%. Absence is spelled N/A instead, which is the same representation
    // the ROI Projections cap rate uses for "no rate", and the row says which
    // kind of absence it is.
    const YieldCell = field => props => {
        const value = props.data[field];
        if (isPresent(value))
            return <span>{(Number(value) * 100).toFixed(2)}%</span>;
        if (partYear)
            return <span className="text-secondary">N/A</span>;
        // Which kind of absence, off the two count columns the row already
        // carries. Outside the yield set there are exactly two cases, and
        // CommittedCapitalPropertyCount separates them: bought and awaiting
        // handover, or sold. The row is not asked for a Sold column it does
        // not have.
        return (
            <div>
                <div className="text-secondary">N/A</div>
                <div className="fs-7 text-secondary">
                    {Number(props.data["CommittedCapitalPropertyCount"]) === 1
                        ? "pre-handover"
                        : "sold"}
                </div>
            </div>
        );
    };

    // Capital columns. Reported, never netted: they are not in NOI and a reader
    // must not be able to take them for an expense inside it. Four things keep
    // them apart - their own band, whose caption says so; the muted background
    // and text that cssClass puts on every cell and header in the band; the
    // two-pixel rule down the left of the band, which is where the NOI
    // arithmetic stops; and their position, after NOI rather than among the
    // expense columns. Both the background/text pair and the edge rule carry
    // !important, so both survive the DevExtreme cell rules and the row
    // alternation underneath them.
    //
    // The edge rule is NOT a Bootstrap utility. border-start is sided but
    // border-2 and border-dark are not - both are border-width / border-color on
    // all four edges with !important - so together they repainted each cell's
    // theme bottom rule as 2px near-black as well as its left one. On the band
    // header that bottom rule spans the whole band, which put a heavy black line
    // under CAPITAL MOVEMENTS out to the right edge of PURCHASE / SALE COSTS,
    // and the sub-header drew a second one directly below it. The replacement
    // sets border-left alone, at the weight and colour the grid already uses on
    // the pinned Property column's edge - global.css .noi-capital-band-edge.
    const CAPITAL_CLASS = "bg-light text-secondary";
    const CAPITAL_EDGE = "bg-light text-secondary noi-capital-band-edge";

    const summaryFigure = (heading, field, note) => {
        const value = summary.figures ? summary.figures[field] : null;
        const negative = isPresent(value) && Number(value) < 0;
        return (
            <Card className="card-light p-3 border-0 h-100">
                <div className={"fs-4 fw-bold mb-1" + (negative && !partYear ? " text-danger" : "")}>
                    AED {money(value)}
                </div>
                <div className="text-secondary text-uppercase">{heading}</div>
                <div className="text-secondary fs-7 mt-1">
                    {partYear ? "Part year - 1 Jan to " + coveredTo : note}
                </div>
                {negative && partYear &&
                    <div className="text-secondary fs-7">Part-year timing, not a loss</div>}
            </Card>
        );
    };

    const marginCard = () => {
        const figures = summary.figures || {};
        const negative = isPresent(figures["NOI"]) && Number(figures["NOI"]) < 0;
        return (
            <Card className="card-light p-3 border-0 h-100">
                <div className={"fs-4 fw-bold mb-1" + (negative && !partYear ? " text-danger" : "")}>
                    {marginText(figures["NOI"], figures["OperatingIncomeTotal"])}
                </div>
                <div className="text-secondary text-uppercase">NOI margin</div>
                <div className="text-secondary fs-7 mt-1">
                    {partYear
                        ? "Part year - not comparable with a full year"
                        : "NOI as a share of operating income"}
                </div>
                {negative && partYear &&
                    <div className="text-secondary fs-7">Part-year timing, not a loss rate</div>}
            </Card>
        );
    };

    // Why a yield is not being shown, in the words the operator needs rather
    // than a symbol. A part year is the common case and it is NOT a small
    // version of a full year - rent arrives on the tenancy anniversary and
    // short-term bookings cluster by start date, so eight months of a year can
    // legitimately contain none of it. Nothing is annualised to fill the gap.
    const yieldSuppressedShort = () => (partYear
        ? "Not shown – part year"
        : "Not shown – no yield set");
    const yieldSuppressedReason = () => (partYear
        ? "Figures cover 1 January to " + coveredTo + " only. Rent is recognised on the "
        + "cheque date and short-term rental on the booking start date, so a part year is "
        + "not a smaller version of a full one. Nothing is annualised, so no percentage is "
        + "shown."
        : "No held, available property under this filter carries an acquisition cost, so "
        + "there is nothing to divide by.");

    // Yield on cost. Denominator is acquisition cost - purchase price plus the
    // brokerage, land-department, trustee and registration fees recorded
    // against it - for properties held, available and therefore able to earn.
    // Sold properties and capital not yet in service carry no yield at all and
    // are excluded rather than counted as zero.
    const yieldCard = (heading, ratioField, numeratorField, note) => {
        const figures = summary.figures || {};
        const value = yieldPercent(figures, ratioField, numeratorField);
        return (
            <Card className="card-light p-3 border-0 h-100">
                {value === null
                    ? <div className="fs-6 fw-bold mb-1 text-secondary">{yieldSuppressedShort()}</div>
                    : <div className="fs-4 fw-bold mb-1">{percentText(value)}</div>}
                <div className="text-secondary text-uppercase">{heading}</div>
                <div className="text-secondary fs-7 mt-1">
                    {value === null ? yieldSuppressedReason() : note}
                </div>
                {value !== null &&
                    <div className="text-secondary fs-7">
                        On AED {money(figures["YieldDenominator"])} of acquisition cost across{" "}
                        {figures["YieldPropertyCount"]} {propertyWord(figures["YieldPropertyCount"])}
                    </div>}
            </Card>
        );
    };

    // The operating-cost drag Nexus asked for in s1.3: how much of the gross
    // yield the running costs take. It is the DIFFERENCE of the two yields and
    // its unit is percentage points, not per cent - subtracting one percentage
    // from another does not give a third percentage, and calling it one invites
    // the reader to treat it as a share.
    //
    // Computed from the two displayed percentages rather than from a third
    // ratio, so the three figures on the strip always reconcile: whatever is
    // shown for gross minus whatever is shown for net is exactly what is shown
    // here. Both must be present, because a drag is meaningless without them.
    const gapCard = () => {
        const figures = summary.figures || {};
        const gross = yieldPercent(figures, "GrossYieldOnCost", "YieldNumeratorIncome");
        const net = yieldPercent(figures, "NetYieldOnCost", "YieldNumeratorNOI");
        const gap = (gross === null || net === null) ? null : gross - net;
        return (
            <Card className="card-light p-3 border-0 h-100">
                {gap === null
                    ? <div className="fs-6 fw-bold mb-1 text-secondary">{yieldSuppressedShort()}</div>
                    : <div className="fs-4 fw-bold mb-1">{gap.toFixed(2)} pp</div>}
                <div className="text-secondary text-uppercase">Operating cost drag</div>
                <div className="text-secondary fs-7 mt-1">
                    {gap === null
                        ? yieldSuppressedReason()
                        : "Gross yield less net yield, in percentage points"}
                </div>
                {gap !== null &&
                    <div className="text-secondary fs-7">
                        {percentText(gross)} gross less {percentText(net)} net
                    </div>}
            </Card>
        );
    };

    // Capital that is held, not sold and NOT yet in service - bought, paid for
    // and not able to earn. It is kept OUT of the yield denominator, because
    // dividing real income by capital that cannot yet produce any understates
    // the yield of the assets that can. Shown so that exclusion is visible
    // rather than silent, and shown on a part year too: it does not move with
    // the calendar. It is never netted off income, expenses or NOI.
    const committedCapitalCard = () => {
        const figures = summary.figures || {};
        const value = figures["CommittedCapitalNotInService"];
        const count = figures["CommittedCapitalPropertyCount"];
        const none = !isPresent(value) || Number(value) === 0;
        return (
            <Card className="card-light p-3 border-0 h-100">
                <div className="fs-4 fw-bold mb-1">AED {money(none ? 0 : value)}</div>
                <div className="text-secondary text-uppercase">Committed capital, not in service</div>
                <div className="text-secondary fs-7 mt-1">
                    Excluded from the yield denominator &ndash; never netted off NOI
                </div>
                <div className="text-secondary fs-7">
                    {none
                        ? "No property under this filter is awaiting handover"
                        : Number(count) + " " + propertyWord(count) + " held and awaiting handover"}
                </div>
            </Card>
        );
    };

    const gridBody = () => {
        if (loading)
            return <div className="text-secondary p-3">Loading NOI&hellip;</div>;
        if (error)
            return (
                <div className="text-danger p-3" role="alert">
                    <div className="fw-bold mb-1">NOI could not be loaded.</div>
                    <div>{error}</div>
                </div>
            );
        if (notBegun)
            return (
                <div className="text-secondary p-3">
                    <div className="fw-bold mb-1">No data for {selection.year}.</div>
                    <div>{notice || "That year has not begun, so no NOI figures exist for it."}</div>
                </div>
            );
        if (propertyRows.length === 0)
            return <div className="text-secondary p-3">No properties carried NOI activity in {selection.year}.</div>;
        if (visibleRows.length === 0)
            return <div className="text-secondary p-3">No properties are held under this company.</div>;
        return (
            <DataGrid
                dataSource={visibleRows}
                keyExpr="ECO_ID"
                height={height}
                showBorders={true}
                showRowLines={true}
                showColumnLines={true}
                columnAutoWidth={true}
                allowColumnResizing={true}
                columnResizingMode="widget"
                rowAlternationEnabled={true}
                hoverStateEnabled={true}
                wordWrapEnabled={true}
            >
                <Paging enabled={false}/>
                <Column caption="Property" dataField="Name" dataType="string" minWidth={240}
                        fixed={true} fixedPosition="left"
                        cellRender={PropertyNameCell}/>
                <Column caption="Owner" dataField="OwnerName" dataType="string" width={120}
                        cellRender={OwnerCell}/>

                <Column caption={"Operating income, AED" + (partYear ? " (" + partYearShort + ")" : "")}>
                    <Column caption="Rent" dataField="OperatingIncomeRent" dataType="number"
                            alignment="right" width={120} cellRender={moneyCell("OperatingIncomeRent")}/>
                    <Column caption="Short-term rental" dataField="OperatingIncomeSTR" dataType="number"
                            alignment="right" width={130} cellRender={moneyCell("OperatingIncomeSTR")}/>
                    <Column caption="Total" dataField="OperatingIncomeTotal" dataType="number"
                            alignment="right" width={130} cssClass="fw-bold"
                            cellRender={moneyCell("OperatingIncomeTotal")}/>
                </Column>

                <Column caption={"Operating expenses, AED" + (partYear ? " (" + partYearShort + ")" : "")}>
                    <Column caption="Maintenance" dataField="OperatingExpenseMaintenance" dataType="number"
                            alignment="right" width={130}
                            cellRender={moneyCell("OperatingExpenseMaintenance")}/>
                    <Column caption="Agency fees" dataField="OperatingExpenseAgencyFees" dataType="number"
                            alignment="right" width={120}
                            cellRender={moneyCell("OperatingExpenseAgencyFees")}/>
                    <Column caption="STR withheld" dataField="OperatingExpenseSTRWithheld" dataType="number"
                            alignment="right" width={130}
                            cellRender={moneyCell("OperatingExpenseSTRWithheld")}/>
                    <Column caption="Total" dataField="OperatingExpenseTotal" dataType="number"
                            alignment="right" width={130} cssClass="fw-bold"
                            cellRender={moneyCell("OperatingExpenseTotal")}/>
                </Column>

                <Column caption={"NOI, AED" + (partYear ? " (" + partYearShort + ")" : "")}
                        dataField="NOI" dataType="number"
                        alignment="right" width={partYear ? 175 : 145}
                        defaultSortIndex={0} defaultSortOrder="desc"
                        cellRender={NoiCell}/>

                {/*  Narrower than the money columns beside them. A percentage
                     is four or five characters against a money column's ten,
                     and the two of them together have to fit in what the
                     capital band was NOT using at 1920 - the grid filled the
                     viewport exactly before these were added, so anything
                     wider pushes the capital band off the right edge, and that
                     band is the one the operator must be able to see is
                     separate from NOI.

                     Narrower again in a part year, because the cells hold only
                     N/A then and the NOI column beside them widens by 30 to
                     carry its part-year caption. Both measured at 1920: the
                     grid's scroll width is 1852 against a client width of
                     1852 on a full year and 1850 on a part year, so neither
                     view scrolls sideways and the page itself never does. */}
                <Column caption={"Yield on cost" + (partYear
                    ? " - not shown for a part year"
                    : " - over acquisition cost")}>
                    <Column caption="Gross" dataField="GrossYieldOnCost" dataType="number"
                            alignment="right" width={partYear ? 60 : 75}
                            cellRender={YieldCell("GrossYieldOnCost")}/>
                    <Column caption="Net" dataField="NetYieldOnCost" dataType="number"
                            alignment="right" width={partYear ? 60 : 75}
                            cellRender={YieldCell("NetYieldOnCost")}/>
                </Column>

                <Column caption="Capital movements, AED - reported here, excluded from NOI"
                        cssClass={CAPITAL_EDGE}>
                    <Column caption="Capital expenditure" dataField="CapitalExpenditure" dataType="number"
                            alignment="right" width={150} cssClass={CAPITAL_EDGE}
                            cellRender={moneyCell("CapitalExpenditure")}/>
                    <Column caption="Purchase / sale costs" dataField="CapitalPurchaseSale" dataType="number"
                            alignment="right" width={155} cssClass={CAPITAL_CLASS}
                            cellRender={moneyCell("CapitalPurchaseSale")}/>
                </Column>
            </DataGrid>
        );
    };

    return (
        <>
            <div
                className="search-actions-header d-flex flex-wrap flex-lg-nowrap justify-content-between align-items-center px-3 pt-3 p-lg-3 bg-white border-bottom">
                <div className="d-sm-flex justify-content-between align-items-center flex-fill mb-3 mb-lg-0">
                    <h2 className="fs-5 fw-medium mb-3 mb-sm-0 flex-shrink-0 me-4">
                        Net Operating Income
                        {partYear &&
                            <span className="badge bg-warning text-dark fs-7 ms-2 align-middle">
                                {selection.year} part year &ndash; to {coveredTo}
                            </span>}
                    </h2>
                    <div className="d-flex align-items-center justify-content-sm-end flex-fill me-lg-1">
                        <span className="text-secondary flex-shrink-0 me-2">Calendar year</span>
                        <InputGroup className="me-3 search-input">
                            <SelectBox items={years}
                                       value={selection.year}
                                       inputAttr={{class: "form-control"}}
                                       elementAttr={{class: "search-input input-group"}}
                                       width="100%"
                                       onValueChanged={yearChanged}
                            ></SelectBox>
                        </InputGroup>
                    </div>
                </div>
            </div>
            <div className="main-wrapper p-3">
                <Card className="p-3 border-0 dashboard-border">
                    {/* The part-year caution, in the page body and above the
                        figures rather than only in the footnote. The text is the
                        server's own Message, so the screen and the endpoint
                        cannot drift apart. */}
                    {!loading && !error && partYear &&
                        <div className="alert alert-warning py-2 px-3 mb-3" role="alert">
                            <b>{selection.year} is a part year.</b>{" "}
                            {notice || ("Figures cover 1 January to " + coveredTo +
                                " only, on a cash-timing basis, and are not comparable with a full year.")}
                            {" "}Nothing below is annualised or prorated.
                        </div>}

                    {!loading && !error && !notBegun &&
                        <>
                            <div className="text-secondary mb-2">
                                {summary.scope} &middot; {selection.year}
                                {partYear && <span> &middot; part year to {coveredTo}</span>}
                            </div>
                            <div className="row mb-3">
                                <div className="col-12 col-md-6 col-xl-3 mb-3 mb-xl-0">
                                    {summaryFigure("Operating income", "OperatingIncomeTotal",
                                        "Rent and gross short-term rental")}
                                </div>
                                <div className="col-12 col-md-6 col-xl-3 mb-3 mb-xl-0">
                                    {summaryFigure("Operating expenses", "OperatingExpenseTotal",
                                        "Maintenance, agency fees, STR withheld")}
                                </div>
                                <div className="col-12 col-md-6 col-xl-3 mb-3 mb-md-0">
                                    {summaryFigure("Net operating income", "NOI",
                                        "Operating income less operating expenses")}
                                </div>
                                <div className="col-12 col-md-6 col-xl-3">
                                    {marginCard()}
                                </div>
                            </div>

                            {/* Yield on cost. A second strip rather than four
                                more cells on the first: these are measured
                                against ACQUISITION COST over the properties in
                                service, while everything above is measured over
                                the year's activity across every property, and
                                the two sets are not the same size. Under ALL the
                                first strip covers 24 properties and this one
                                covers 14. */}
                            <div className="text-secondary mb-2">
                                Yield on cost &middot; acquisition cost basis &middot;{" "}
                                held and available properties only
                            </div>
                            <div className="row mb-3">
                                <div className="col-12 col-md-6 col-xl-3 mb-3 mb-xl-0">
                                    {yieldCard("Gross yield on cost", "GrossYieldOnCost",
                                        "YieldNumeratorIncome",
                                        "Operating income over acquisition cost")}
                                </div>
                                <div className="col-12 col-md-6 col-xl-3 mb-3 mb-xl-0">
                                    {yieldCard("Net yield on cost", "NetYieldOnCost",
                                        "YieldNumeratorNOI",
                                        "NOI over acquisition cost")}
                                </div>
                                <div className="col-12 col-md-6 col-xl-3 mb-3 mb-md-0">
                                    {gapCard()}
                                </div>
                                <div className="col-12 col-md-6 col-xl-3">
                                    {committedCapitalCard()}
                                </div>
                            </div>
                        </>}

                    {/* No tab strip on a year with nothing in it - an ALL tab
                        reading 0 is the zero portfolio this screen must not
                        show. */}
                    {!notBegun && <div
                        className="d-flex flex-wrap flex-xl-nowrap justify-content-between align-items-center mb-0 mb-md-4">
                        {/* Tabs title */}
                        <ul className="nav nav-tabs fs-6 nav-tab tab-sm inner-tab mb-3 mb-xl-0">
                            {tabs.map(item => {
                                return (<li key={item.key} className="nav-item" onClick={() => {
                                    setCurrentTab(item.key)
                                }}>
                                    <a className={currentTab === item.key ? "nav-link active" : "nav-link"}
                                       data-bs-toggle="tab"
                                       eventKey={item.key}
                                       href={'#' + item.key}>{item.name}<span
                                        className="tab-count"
                                    >{item.count}</span></a>
                                </li>)
                            })}
                        </ul>
                    </div>}
                    <div className="tab-content pt-1" ref={gridAreaRef}>
                        {gridBody()}
                        <div className="text-secondary fs-7 mt-2" ref={noteRef}>
                            Operating income is rent and gross short-term rental; operating expenses are maintenance,
                            agency fees and short-term rental withheld costs; NOI is the first less the second. Capital
                            expenditure and purchase or sale costs are excluded from NOI and shown separately - they are
                            reported, never netted. All figures are AED, converted at the rate stored on each
                            transaction, and short-term rental income is attributed to the booking start date. Owner is
                            the current default-owner mapping rather than ownership as at the transaction date, the same
                            basis the property listing tabs use, so the September 2025 Cavoria transfer does not move
                            historical figures between company columns.
                            {" "}Yield on cost divides operating income (gross) and NOI (net) by acquisition cost -
                            the purchase price together with the brokerage, land-department, trustee, notary and
                            registration fees recorded against it, converted to AED at the property's current rate. It
                            covers properties that are held and available only: sold properties and capital not yet in
                            service carry no yield and read N/A rather than zero, while a held property that earned
                            nothing reads a real 0.00%. The portfolio yield is total income over total cost across
                            those properties, not the average of the property percentages, so it is not the NOI figure
                            above divided by anything shown here. Committed capital not in service is reported beside
                            it and is never netted off NOI. In a part year no yield is shown at all and nothing is
                            annualised.
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );
}

export default NOI;
